import { handOver, stayed } from "@/components/shell/record";

/**
 * Signing out, once: delete the session row, then land on the sign-in page.
 *
 * Shared by the two places that do it — the menu under the top bar's tool
 * (components/shell/SignOutMenu) and the button on `/logout`, which billing
 * and deleting an account still send people to — so that there is one
 * request and one landing, not two copies that drift.
 *
 * A POST and not a link, because signing out is a write: a link that ends a
 * session is a link something else can follow on your behalf, and the row
 * has to go on the server, not merely the cookie in this browser (which is
 * what "clear cookies" would leave behind).
 *
 * Only a request that succeeded leaves. It used to go to `/login` whatever
 * happened, on the belief that the proxy would send a browser without a
 * session there anyway — but after a failed request the browser still has
 * one, `/login` is a public page that draws for a signed-in visitor too, and
 * the person walks away from a shared machine looking at a sign-in form while
 * their account is still open behind it. A failure is `false`, and the
 * caller says so where they pressed.
 *
 * Before the request, the page's record is handed over (Issue 804): the page
 * they were on is left and what waits is delivered while the browser still
 * says who they are. Sent after, it would arrive as nobody's — every
 * sign-out left a stray `page_left`, filed as nobody's, in their session. A
 * sign-out that fails gives the record back (`stayed`).
 */
export async function leave(): Promise<boolean> {
  await handOver();
  const res = await fetch("/api/auth/logout", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: "{}",
  }).catch(() => null);
  if (!res?.ok) {
    stayed();
    return false;
  }
  window.location.href = "/login";
  return true;
}
